import { AnyAction } from '@reduxjs/toolkit'
import { subMonths, subDays } from 'date-fns'
import { Epic } from 'redux-observable'
import { of, from } from 'rxjs'
import { switchMap, map, catchError, withLatestFrom, filter } from 'rxjs/operators'

import {
  fetchBalance,
  fetchBalanceSuccess,
  fetchBalanceError,
  fetchRecharges,
  fetchMoreRecharges,
  fetchRechargesSuccess,
  fetchMoreRechargesSuccess,
  fetchRechargesError,
} from './balance.reducer'

import { BalanceService } from '../services/balance.service'

import { ServiceError } from '~/common/model'
import { RootState } from '~/core/store/root.reducer'
import { formatDateToString } from '~/utils'

const fetchBalanceEpic: Epic<
  AnyAction,
  AnyAction,
  RootState,
  { balanceService: BalanceService }
> = (action$, state$, { balanceService }) =>
  action$.pipe(
    filter(fetchBalance.match),
    withLatestFrom(state$),
    switchMap(([action]) =>
      from(balanceService.fetchBalance(action.payload)).pipe(
        map((result) => fetchBalanceSuccess(result)),
        catchError((error: ServiceError) => {
          return of(
            fetchBalanceError({
              subscriptionId: action.payload.subscriptionId,
              error: {
                errorMessage: error?.response?.statusText,
                errorCode: error?.response?.status,
                id: '',
              },
            }),
          )
        }),
      ),
    ),
  )

const fetchRechargesEpic: Epic<
  AnyAction,
  AnyAction,
  RootState,
  { balanceService: BalanceService }
> = (action$, state$, { balanceService }) =>
  action$.pipe(
    filter(fetchRecharges.match),
    withLatestFrom(state$),
    switchMap(([action]) =>
      from(balanceService.fetchRecharges(action.payload)).pipe(
        map((result) => fetchRechargesSuccess(result)),
        catchError((error: ServiceError) => {
          return of(
            fetchRechargesError({
              subscriptionId: action.payload.subscriptionId,
              error: {
                errorMessage: error?.response?.statusText,
                errorCode: error?.response?.status,
                id: '',
              },
            }),
          )
        }),
      ),
    ),
  )

const fetchMoreRechargesEpic: Epic<
  AnyAction,
  AnyAction,
  RootState,
  { balanceService: BalanceService }
> = (action$, state$, { balanceService }) =>
  action$.pipe(
    filter(fetchMoreRecharges.match),
    withLatestFrom(state$),
    switchMap(([action]) => {
      const { monthsToAdd, subscriptionId } = action.payload
      const startDate = formatDateToString(
        subMonths(
          new Date(state$.value.balance[subscriptionId].recharges.data.periodDate.startDate),
          monthsToAdd,
        ),
      )
      const endDate = formatDateToString(
        subDays(
          new Date(state$.value.balance[subscriptionId].recharges.data.periodDate.startDate),
          1,
        ),
      )
      const fetchRechargesProps = {
        subscriptionId: action.payload.subscriptionId,
        brand: action.payload.brand,
        sub: action.payload.sub,
        startDate,
        endDate,
      }
      return from(balanceService.fetchRecharges(fetchRechargesProps)).pipe(
        map((result) => fetchMoreRechargesSuccess(result)),
        catchError((error: ServiceError) => {
          return of(
            fetchRechargesError({
              subscriptionId: action.payload.subscriptionId,
              error: {
                errorMessage: error?.response?.statusText,
                errorCode: error?.response?.status,
                id: '',
              },
            }),
          )
        }),
      )
    }),
  )

const balanceAndRechargesEpic = {
  fetchBalanceEpic,
  fetchRechargesEpic,
  fetchMoreRechargesEpic,
}

export default balanceAndRechargesEpic
